import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getGuestStore } from "../../../utils/demoApi";

/**
 * useAttendeeToken — absorb the credential arriving in a resume link.
 *
 * Every message we send an attendee links to `.../{slug}/join?t=<token>`. This
 * hook is what turns that query parameter into a logged-in-feeling visitor: it
 * stores the token under the same per-kind key the registration form writes, so
 * every page downstream reads it exactly as if they had just filled the form on
 * this device.
 *
 * ⚠️ **It strips `?t=` from the address bar immediately.** The token is a bearer
 * credential; leaving it in `window.location` puts it in the `Referer` header of
 * every subsequent request the page makes — and these pages load third-party
 * script (Cloudflare Turnstile, the Zoom Web SDK) from other origins. A
 * `replaceState` costs nothing and closes that. It also stops the credential
 * being copied into a support ticket along with the URL, or landing in the
 * browser history of a shared machine.
 *
 * Runs during render rather than in an effect on purpose: the pages that use
 * this decide what to render from the stored token, and a one-frame gap would
 * flash the registration form at somebody who is already registered.
 *
 * @param {'demo'|'webinar'} kind
 * @param {string} slug
 * @returns {string|null} the attendee's token, from the URL or from storage
 */
export default function useAttendeeToken(kind, slug) {
  const { search } = useLocation();

  return useMemo(() => {
    const store = getGuestStore(kind);
    if (!slug) return null;

    let fromUrl = null;
    try {
      fromUrl = new URLSearchParams(search).get("t");
    } catch { /* malformed query — fall through to storage */ }

    if (fromUrl) {
      store.save(slug, { token: fromUrl, savedAt: Date.now(), viaLink: true });
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("t");
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      } catch { /* history unavailable — the token is stored either way */ }
      return fromUrl;
    }

    return store.load(slug)?.token || null;
  }, [kind, slug, search]);
}
